import { Bars3Icon } from "@heroicons/react/24/outline";
import { PipelineId } from "@sp-crm/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    PipelineStageSummary,
    PipelineSummary,
} from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { fancyAlert } from "components/ui/fancy-alert";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { produce } from "immer";
import React, { useCallback } from "react";
import {
    calculateNewOrder,
    useReorderableListDrag,
    useReorderableListDrop,
} from "util/dnd";
import { handleEvent } from "util/user-events";

interface ReferralPipelineStageEditorProps {
    pipeline: PipelineSummary;
    onChanged: () => void;
}

export const ReferralPipelineStageEditor: React.FC<
    ReferralPipelineStageEditorProps
> = props => {
    const { pipeline, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const [newStageName, setNewStageName] = React.useState("");
    const createStageMutation = useMutation({
        ...trpcClient.pipeline.createStage.mutationOptions(),
    });
    const setStageOrdersMutation = useMutation({
        ...trpcClient.pipeline.setStageOrders.mutationOptions(),
    });

    const handleReorder = useCallback(
        (draggingIndex: number, targetIndex: number) => {
            queryClient.setQueryData(
                trpcClient.pipeline.list.queryKey(),
                (oldData: PipelineSummary[] | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }
                    return produce(oldData, draft => {
                        const draftPipeline = draft.find(p => p.id === pipeline.id);
                        if (!draftPipeline) {
                            return;
                        }
                        const newOrder = calculateNewOrder(
                            draftPipeline.stages,
                            draggingIndex,
                            targetIndex,
                        );
                        const current = draftPipeline.stages[draggingIndex];
                        const actual = draftPipeline.stages.find(
                            s => s.id === current.id,
                        );
                        if (!actual) {
                            return;
                        }
                        actual.order = newOrder;
                        draftPipeline.stages.sort((a, b) => a.order - b.order);
                    });
                },
            );
        },
        [queryClient, trpcClient, pipeline.id],
    );

    const handleCommitOrder = useCallback(async () => {
        const pipelines = queryClient.getQueryData<PipelineSummary[]>(
            trpcClient.pipeline.list.queryKey(),
        );
        const currentPipeline = pipelines?.find(p => p.id === pipeline.id);
        if (!currentPipeline || currentPipeline.stages.length === 0) {
            return;
        }
        await setStageOrdersMutation.mutateAsync({
            orders: currentPipeline.stages.map((stage, index) => ({
                stageId: stage.id,
                order: index,
            })),
        });
        handleEvent("referral-pipeline-stage-reorder", {
            stageCount: currentPipeline.stages.length,
        });
        onChanged();
    }, [queryClient, trpcClient, pipeline.id, setStageOrdersMutation, onChanged]);

    const handleRevertOrder = useCallback(() => {
        onChanged();
    }, [onChanged]);

    const handleNewStageNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setNewStageName(e.target.value);
        },
        [setNewStageName],
    );

    const stageNameInputRef = React.useRef<HTMLInputElement>(null);

    const addStage = useCallback(async () => {
        const name = newStageName.trim();
        if (!name || createStageMutation.isPending) {
            return;
        }
        await createStageMutation.mutateAsync({
            pipelineId: pipeline.id,
            name,
        });
        handleEvent("referral-pipeline-stage-create");
        setNewStageName("");
        onChanged();
        stageNameInputRef.current?.focus();
    }, [newStageName, createStageMutation, pipeline.id, setNewStageName, onChanged]);

    const handleAddStage = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await addStage();
        },
        [addStage],
    );

    const handleStageNameKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                await addStage();
            }
        },
        [addStage],
    );

    return (
        <div className="space-y-4">
            <ul className="divide-y divide-gray-200">
                {pipeline.stages.map((stage, index) => (
                    <ReferralPipelineStageRow
                        key={stage.id}
                        pipelineId={pipeline.id}
                        stage={stage}
                        index={index}
                        commitOrder={handleCommitOrder}
                        revertOrder={handleRevertOrder}
                        reorder={handleReorder}
                        onChanged={onChanged}
                    />
                ))}
            </ul>
            <div className="flex items-center space-x-4">
                <p className="w-7">&nbsp;</p>
                <div className="w-96">
                    <Input
                        ref={stageNameInputRef}
                        placeholder="Enter stage name"
                        value={newStageName}
                        onChange={handleNewStageNameChange}
                        onKeyDown={handleStageNameKeyDown}
                    />
                </div>
                <PrimaryButton onClick={handleAddStage}>Add stage</PrimaryButton>
            </div>
        </div>
    );
};

interface ReferralPipelineStageRowProps {
    pipelineId: PipelineId;
    stage: PipelineStageSummary;
    index: number;
    commitOrder: () => void;
    revertOrder: () => void;
    reorder: (draggingIndex: number, targetIndex: number) => void;
    onChanged: () => void;
}

const ReferralPipelineStageRow: React.FC<ReferralPipelineStageRowProps> = props => {
    const { pipelineId, stage, index, commitOrder, revertOrder, reorder, onChanged } =
        props;
    const trpcClient = trpc.useTRPC();
    const renameMutation = useMutation({
        ...trpcClient.pipeline.renameStage.mutationOptions(),
    });
    const deleteMutation = useMutation({
        ...trpcClient.pipeline.deleteStage.mutationOptions(),
    });

    const ref = React.useRef<HTMLLIElement>(null);
    const type = `referral-pipeline-stages-${pipelineId}`;
    const [{ isDragging }, drag, preview] = useReorderableListDrag({
        type,
        index,
        commitOrder,
        revertOrder,
        id: stage.id,
    });
    const [, drop] = useReorderableListDrop({
        ref,
        index,
        reorder,
        type,
    });
    drop(ref);

    const handleNameChange = useCallback(
        async (name: string) => {
            const trimmed = name.trim();
            if (!trimmed) {
                return;
            }
            await renameMutation.mutateAsync({ stageId: stage.id, name: trimmed });
            handleEvent("referral-pipeline-stage-rename");
            onChanged();
        },
        [renameMutation, stage.id, onChanged],
    );

    const handleDelete = useCallback(async () => {
        try {
            await deleteMutation.mutateAsync({ stageId: stage.id });
            handleEvent("referral-pipeline-stage-delete");
            onChanged();
        } catch (error) {
            await fancyAlert(
                "Cannot delete stage",
                error instanceof Error ? error.message : "Unable to delete this stage",
            );
        }
    }, [deleteMutation, stage.id, onChanged]);

    return (
        <li className="py-3" ref={ref}>
            <div
                ref={preview}
                className={`flex space-x-4 items-center ${
                    isDragging ? "opacity-0" : ""
                }`}>
                <div className="cursor-move px-1 py-3" ref={drag}>
                    <Bars3Icon className="w-5 h-5 text-gray-500" />
                </div>
                <div className="w-96">
                    <AutosavingInput initial={stage.name} onCommit={handleNameChange} />
                </div>
                <div className="w-8">
                    <DeleteButton
                        backgroundColor="bg-white"
                        confirm={{
                            title: "Delete stage",
                            message: `Are you sure you want to delete the "${stage.name}" stage?`,
                        }}
                        onClick={handleDelete}
                    />
                </div>
            </div>
        </li>
    );
};
